/** @format */
import React from 'react';
import { Box, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CloseIcon from '@material-ui/icons/Close';
import CancelCreateTicketModal from './cancel';
import SuccessCreateTicket from './success';
import ErrorCreateTicket from './error';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/button';
import useAuth from 'src/hooks/useAuth';
import SelectComponent from 'src/components/select';
import { useDispatch, useSelector } from 'src/store';
import { clearTicketError, createTicket, getCompanyTickets, getTickets } from 'src/slices/tickets';
import { VALIDATE_REGEX } from 'src/constants';
import { UploadField } from 'src/components/upload/upload';
import UpdateTicketLoading from './updateTicketLoading';

const initPostData = {
  title: null,
  contactName: null,
  contactEmail: null,
  requestType: null,
  dealerAssociation: null,
  impact: null,
  urgency: null,
  typeOfProblem: null,
  category: null,
  endCustomerAccount: null,
  feature: null,
  affectedEmailAddress: null,
  affectedVehicle: null,
  affectedDriver: null,
  model: null,
  softwareVersion: null,
  deviceserialnumber: null,
  vehicleRegistrationNumber: null,
  type: null,
  companyCardNumber: null,
  description: null,
  attachments: null,
  attachmentFileNames: null,
  dateOfOccurence: null,
  timeOfOccurence: null,
  project: null,
  region: null,
  ava_endcustomer: null,
  ava_endcustomercontact: null,
  eHorizonVersion: null
};

const mapOnDemandProjectMapping = {
  'maps-on-demand-customer-mitsubishi': 'Mitsubishi',
  'maps-on-demand-customer-renault': 'Renault',
  'maps-on-demand-customer-nissan-isa': 'Nissan ISA',
  'maps-on-demand-customer-nissan-tiny': 'Nissan Tiny'
};

const getInitialTicketData = (user, userData, impactData, urgencyData) => ({
  ...initPostData,
  customerId: user.company || 'B2C dummy',
  endCustomerAccount: user.firstName + ' ' + user.lastName,
  accountName: user.firstName + ' ' + user.lastName,
  zip: user.address?.postalCode || userData.address?.postalCode || '',
  contactName: user.firstName + ' ' + user.lastName,
  contactEmail: user.email,
  affectedEmailAddress: user.email,
  ava_endcustomer: user.firstName + ' ' + user.lastName,
  ava_endcustomercontact: user.email,
  project: user.scenario ? mapOnDemandProjectMapping[user.scenario] || null : null,
  impact: '',
  urgency: '',
  region: userData.scenario && !userData.scenario.includes('nissan') ? 'Europe' : null
});

export default function CreateTicketForMoDModal({ open, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { userData } = useSelector(state => state.users);
  const { updateTicketLoading, error: errorTicket } = useSelector(state => state.tickets);

  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isOtherPerson, setIsOtherPerson] = React.useState(false);
  const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [postData, setPostData] = React.useState(initPostData);
  const [modCategoryData, setModCategoryData] = React.useState([]);
  const [validations, setValidations] = React.useState({});
  const [files, setFiles] = React.useState([]);
  const [fileKey, setFileKey] = React.useState(Math.random().toString(36));
  const [impactData, setImpactData] = React.useState([
    { value: 'Significant', name: t('Tickets.impactSignificant') },
    { value: 'Moderate', name: t('Tickets.impactModerate') },
    { value: 'Minor', name: t('Tickets.impactMinor') }
  ]);
  const [urgencyData, setUrgencyData] = React.useState([
    { value: 'High', name: t('Tickets.urgencyHigh') },
    { value: 'Medium', name: t('Tickets.urgencyMedium') },
    { value: 'Low', name: t('Tickets.urgencyLow') }
  ]);

  const initImpactData = React.useRef(impactData);
  const initUrgencyData = React.useRef(urgencyData);
  const regionData = React.useRef([
    { value: 'Europe', name: t('Tickets.regionEurope') },
    { value: 'North America', name: t('Tickets.regionNorthAmerica') },
    { value: 'Japan', name: t('Tickets.regionJapan') }
  ]);

  // Reset ticket data whenever modal opens or user/userData is updated
  const restartProcess = React.useCallback(() => {
    if (user && userData) {
      setPostData(getInitialTicketData(user, userData, initImpactData.current, initUrgencyData.current));
      setFiles([]);
      setFileKey(Math.random().toString(36));
      setImpactData([...initImpactData.current]);
      setUrgencyData([...initUrgencyData.current]);
      setValidations({});
    }
  }, [user, userData]);

  React.useEffect(() => {
    if (user && userData) {
      restartProcess();
      setIsAdmin(user.roles?.includes('admin'));
    }
  }, [open, user, userData, restartProcess]);

  // Clear error when errorTicket updates
  React.useEffect(() => {
    if (errorTicket) setSuccess(false);
  }, [errorTicket]);

  // Reset postData after success is completed
  React.useEffect(() => {
    if (success && !updateTicketLoading) {
      setPostData(initPostData);
    }
  }, [success, updateTicketLoading]);

  // Update modCategory data based on requestType
  React.useEffect(() => {
    if (!postData.requestType || postData.requestType === 'Task' || postData.requestType === 'Incident') {
      setModCategoryData([
        { value: 'Account Creation', name: t('Tickets.modCategoryOption1') },
        { value: 'API Status', name: t('Tickets.modCategoryOption2') },
        { value: 'Data Availability', name: t('Tickets.modCategoryOption3') },
        { value: 'Data Connectivity Issue', name: t('Tickets.modCategoryOption4') },
        { value: 'EhProvider (embedded)', name: t('Tickets.modCategoryOption5') },
        { value: 'License Issues', name: t('Tickets.modCategoryOption6') },
        { value: 'Map Issue', name: t('Tickets.modCategoryOption7') }
      ]);
    }
  }, [postData.requestType, t]);

  // Set default region if applicable
  React.useEffect(() => {
    if (userData?.scenario && !userData.scenario.includes('nissan')) {
      setPostData(pd => ({ ...pd, region: 'Europe' }));
    }
  }, [userData]);

  // Update postData when isOtherPerson toggles
  React.useEffect(() => {
    setValidations({});
    if (isOtherPerson) {
      setPostData(pd => ({
        ...pd,
        endCustomerAccount: '',
        contactName: '',
        contactEmail: ''
      }));
    } else if (userData) {
      setPostData(pd => ({
        ...pd,
        endCustomerAccount: userData.company,
        contactName: userData.firstName + ' ' + userData.lastName,
        contactEmail: userData.email
      }));
    }
  }, [isOtherPerson, userData]);

  // Validate form data
  const validate = React.useCallback(async () => {
    let _validations = {
      ...validations,
      requestType: !postData.requestType,
      modCategory: !postData.modCategory,
      title: !postData.title,
      description: !postData.description,
      eHorizonVersion: postData.requestType === 'Incident' ? !postData.eHorizonVersion : false,
      impact: !postData.impact,
      urgency: !postData.urgency,
      contactName: !postData.contactName,
      region: !postData.region,
      contactEmail: !postData.contactEmail || !VALIDATE_REGEX.email.test(postData.contactEmail)
    };
    if (postData.attachments && validations.attachments) {
      _validations = { ..._validations, attachments: validations.attachments };
    }
    setValidations(_validations);
    return _validations;
  }, [postData, validations, files]);

  // Update postData and adjust validations for specific fields
  const updatePostData = (name, value) => {
    if (name === 'requestType') {
      if (value === 'Task') {
        setPostData(pd => ({
          ...pd,
          requestType: value,
          impact: 'Minor',
          region: userData?.scenario && !userData.scenario.includes('nissan') ? 'Europe' : null
        }));
        setImpactData([...initImpactData.current]);
        setUrgencyData([...initUrgencyData.current]);
      } else {
        setPostData(pd => ({
          ...pd,
          requestType: value,
          impact: null,
          region: userData?.scenario && !userData.scenario.includes('nissan') ? 'Europe' : null
        }));
        setImpactData([{ value: 'Extensive', name: 'Extensive' }, ...initImpactData.current]);
        setUrgencyData([{ value: 'Critical', name: 'Critical' }, ...initUrgencyData.current]);
      }
    } else {
      setPostData(pd => ({ ...pd, [name]: value }));
    }
    // Update field-specific validations
    switch (name) {
      case 'requestType':
      case 'modCategory':
      case 'contactName':
      case 'title':
      case 'description':
      case 'impact':
      case 'urgency':
        setValidations(val => ({ ...val, [name]: !value }));
        break;
      case 'region':
        setValidations(val => ({
          ...val,
          [name]: userData?.scenario && userData.scenario.includes('nissan') ? !value : false
        }));
        break;
      case 'eHorizonVersion':
        setValidations(val => ({
          ...val,
          [name]: postData.requestType === 'Incident' ? !value : false
        }));
        break;
      case 'contactEmail':
        setValidations(val => ({
          ...val,
          [name]: !value || !VALIDATE_REGEX.email.test(value)
        }));
        break;
      default:
        break;
    }
  };

  const closeNewTicket = () => {
    if (!updateTicketLoading) {
      onClose(false);
      setCancelModalOpen(false);
      // avoid flash of layout
      setTimeout(() => {
        setPostData(initPostData);
        setError(false);
        setSuccess(false);
        setFiles([]);
        dispatch(clearTicketError());
      }, 300);
    }
  };

  const handleClose = () => {
    if (!updateTicketLoading) {
      success ? closeNewTicket() : setCancelModalOpen(true);
    }
  };

  const handleSubmit = async () => {
    const _validations = await validate();
    if (Object.keys(_validations).length === 0 || Object.values(_validations).every(item => !item)) {
      const incidentDate = postData.dateOfOccurence
        ? postData.dateOfOccurence + (postData.timeOfOccurence ? `T${postData.timeOfOccurence}:00Z` : 'T00:00:00Z')
        : null;
      const createTicketPostData = {
        caseType: postData.requestType,
        title: postData.title,
        description: postData.description,
        customerid: postData.customerId,
        accountName: postData.endCustomerAccount,
        zip: postData.zip,
        contactName: postData.contactName || postData.ava_endcustomer,
        contactEmail: postData.contactEmail || postData.ava_endcustomercontact,
        project: postData.project,
        impact: postData.impact,
        urgency: postData.urgency,
        smy_incidentdateofoccurence: incidentDate ? new Date(incidentDate).toISOString() : null,
        attachments: postData.attachments?.length > 0 ? postData.attachments : null,
        attachmentFileNames: postData.attachmentFileNames?.length > 0 ? postData.attachmentFileNames : null,
        ava_endcustomer: isOtherPerson ? postData.ava_endcustomer : null,
        ava_endcustomercontact: isOtherPerson ? postData.ava_endcustomercontact : null,
        eHorizonVersion: postData.eHorizonVersion,
        region: postData.region || null,
        modCategory: postData.modCategory
      };
      await dispatch(createTicket(createTicketPostData));
      setError(false);
      setSuccess(true);
      setTimeout(() => {
        const lang = window.localStorage.i18nextLng || 'en';
        dispatch(getTickets(lang));
        dispatch(getCompanyTickets(lang));
      }, 500);
    }
  };

  return (
    <>
      <Dialog
        className={`ticket-modal-dialog`}
        fullWidth
        maxWidth="md"
        open={open}
        PaperProps={{
          style: {
            backgroundColor: '#fff',
            borderRadius: 10
          }
        }}
        onClose={handleClose}
        aria-labelledby="max-width-dialog-title">
        <DialogActions>
          <IconButton onClick={handleClose} disabled={updateTicketLoading}>
            <CloseIcon style={{ color: '#000' }} />
          </IconButton>
        </DialogActions>
        <DialogContent>
          <div className="create-ticket-body">
            {errorTicket || error ? (
              <ErrorCreateTicket closeModal={closeNewTicket} />
            ) : success ? (
              <SuccessCreateTicket closeModal={closeNewTicket} />
            ) : (
              <>
                <Typography className="create-ticket-title">{t('Tickets.newTicket')}</Typography>
                <Box className="create-ticket-box">
                  {updateTicketLoading && <UpdateTicketLoading />}
                  <div className="contact-person-heading">{t('Tickets.contactPerson')}</div>
                  {user && (
                    <div className="contact-person">
                      <div className="contact-person-left">
                        <p className="contact-name">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="contact-email">{user.email}</p>
                        <p className="contact-tel">{user.address?.phoneNumber || ''}</p>
                      </div>
                      <div className="contact-person-right">
                        <p className="contact-address">{user.company}</p>
                        {user.address && (
                          <>
                            <p className="contact-street">
                              {user.address.street} {user.address.postBox}
                            </p>
                            <p className="contact-city">
                              {user.address.country} {user.address.postalCode} {user.address.city}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="contact-person-checkbox">
                    {/* TEMPORARY HIDDEN */}
                    {/* <div className="contact-person-checkbox-flex">
                    <input
                      type="checkbox"
                      id="contact-person-checkbox"
                      onChange={() => setIsOtherPerson(!isOtherPerson)}
                      checked={isOtherPerson}
                    />
                    <label htmlFor="contact-person-checkbox">{t('Tickets.checkboxLabelNewTicket')}</label>
                  </div> */}
                    {isOtherPerson && (
                      <Grid container style={{ marginTop: 10 }}>
                        <Grid item xs={12}>
                          <TextField
                            className="create-ticket-text-field"
                            fullWidth
                            id="other-person-name"
                            label={`${t('Tickets.name')} *`}
                            variant="standard"
                            style={{ marginBottom: 12 }}
                            onChange={e => updatePostData('contactName', e.target.value)}
                            value={postData.contactName || ''}
                            error={validations.contactName || false}
                          />
                          <TextField
                            className="create-ticket-text-field"
                            fullWidth
                            id="other-person-email"
                            label={`${t('Tickets.email')} *`}
                            variant="standard"
                            onChange={e => updatePostData('contactEmail', e.target.value)}
                            value={postData.contactEmail || ''}
                            error={validations.contactEmail || false}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </div>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid style={{ marginBottom: 12 }}>
                        <SelectComponent
                          label={`${t('Tickets.requestType')} *`}
                          options={[
                            { value: 'Incident', name: t('Tickets.requestTypeOption1') },
                            { value: 'Task', name: t('Tickets.requestTypeOption2') }
                          ]}
                          onChange={e => updatePostData('requestType', e.target.value)}
                          value={postData.requestType || ''}
                          error={validations.requestType || false}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid style={{ marginBottom: 12 }}>
                        <SelectComponent
                          label={`${t('Tickets.modCategory')} *`}
                          options={modCategoryData}
                          onChange={e => updatePostData('modCategory', e.target.value)}
                          value={postData.modCategory || ''}
                          error={validations.modCategory || false}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid style={{ marginBottom: 12 }}>
                        <TextField
                          className="create-ticket-text-field"
                          label={`${t('Tickets.title')} *`}
                          onChange={e => updatePostData('title', e.target.value)}
                          value={postData.title || ''}
                          error={validations.title || false}
                          inputProps={{ maxLength: 200 }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid style={{ marginBottom: 30 }}>
                      <TextField
                        className="create-ticket-textarea"
                        multiline
                        label={`${t('Tickets.description')} *`}
                        onChange={e => updatePostData('description', e.target.value)}
                        value={postData.description || ''}
                        variant="outlined"
                        minRows={4}
                        error={validations.description || false}
                        inputProps={{ maxLength: 100000 }}
                      />
                    </Grid>
                  </Grid>
                  {postData.requestType === 'Incident' && (
                    <Grid container className="create-ticket-mod-selections">
                      <Grid item xs={12} md={4}>
                        <Grid style={{ marginBottom: 12 }}>
                          <TextField
                            type="date"
                            className="create-ticket-text-field create-ticket-text-field-date"
                            label={`${t('Tickets.incidentDateOfOccurrence')}`}
                            onChange={e => updatePostData('dateOfOccurence', e.target.value)}
                            value={postData.dateOfOccurence || ''}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Grid style={{ marginBottom: 12 }}>
                          <TextField
                            type="time"
                            className="create-ticket-text-field create-ticket-text-field-date"
                            label={`${t('Tickets.incidentTimeOfOccurrence')}`}
                            onChange={e => updatePostData('timeOfOccurence', e.target.value)}
                            value={postData.timeOfOccurence || ''}
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Grid style={{ marginBottom: 12 }}>
                          <TextField
                            className="create-ticket-text-field"
                            label={`${t('Tickets.eHorizonVersion')} *`}
                            onChange={e => updatePostData('eHorizonVersion', e.target.value)}
                            value={postData.eHorizonVersion || ''}
                            error={validations.eHorizonVersion || false}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  <Grid container className="create-ticket-mod-selections">
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 12 }}>
                        <SelectComponent
                          label={`${t('Tickets.impact')} *`}
                          options={impactData}
                          onChange={e => updatePostData('impact', e.target.value)}
                          value={postData.impact || ''}
                          error={validations.impact || false}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Grid style={{ marginBottom: 12 }}>
                        <SelectComponent
                          label={`${t('Tickets.urgency')} *`}
                          options={urgencyData}
                          onChange={e => updatePostData('urgency', e.target.value)}
                          value={postData.urgency || ''}
                          error={validations.urgency || false}
                        />
                      </Grid>
                    </Grid>
                    {userData?.scenario && userData.scenario.includes('nissan') && (
                      <Grid item xs={12} md={4}>
                        <Grid style={{ marginBottom: 12 }}>
                          <SelectComponent
                            label={`${t('Tickets.region')} *`}
                            options={regionData.current}
                            onChange={e => updatePostData('region', e.target.value)}
                            value={postData.region}
                            error={validations.region || false}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                  <Grid container style={{ marginTop: '20px' }}>
                    <UploadField
                      error={validations.attachments}
                      postData={postData}
                      setPostData={setPostData}
                      fileKey={fileKey}
                      setValidations={setValidations}
                    />
                  </Grid>
                </Box>
                <div className="create-ticket-btns">
                  <button disabled={updateTicketLoading} className="btn-restart" onClick={restartProcess}>
                    {t('Tickets.restartProcess')}
                  </button>
                  <Button
                    disabled={updateTicketLoading}
                    borderOnly
                    onClick={handleSubmit}
                    additionalClassName="btn-submit">
                    {updateTicketLoading ? t('personalData.loading') : t('Tickets.submit')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <CancelCreateTicketModal
        onClose={() => setCancelModalOpen(false)}
        open={cancelModalOpen}
        closeNewTicket={closeNewTicket}
      />
    </>
  );
}
